import React from "react";
import {
  Building2,
  Stethoscope,
  CalendarCheck,
  BadgeCheck,
  BookOpen,
} from "lucide-react";
import { motion } from "framer-motion";

const Aboutus = () => {
  return (
    <motion.section
      id="About"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        ease: "easeOut"
      }}
      viewport={{ once: true, amount: 0.2 }}
      className="bg-gradient-to-b from-secondary to-secondary/80 py-[6vw] lg:py-[3vw] relative overflow-hidden"
    >
      {/* Premium decorative background */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-r from-royalBlue/5 to-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-l from-gradient2/5 to-royalBlue/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1400px] mx-auto px-[5%] relative z-10">
        {/* Pill-shaped header - centered on top */}
        {/* Pill Header - Copy this block, change the text only */}
<div className="flex justify-center mb-8 md:mb-10">
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-5 py-2 sm:px-6 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg">
    <BookOpen size={18} className="text-royalGreen" />
    <p className="font-poppins font-semibold text-royalBlue text-sm sm:text-base md:text-lg tracking-wide uppercase whitespace-nowrap">
      ABOUT LIVMO Health
    </p>
  </div>
</div>

        {/* Two column layout: left text, right cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          
          {/* Left side - Explanation Text */}
          <div 
            data-aos="fade-right"
            data-aos-duration="800"
            className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl hover:shadow-royalBlue/10 transition-all duration-500 hover:-translate-y-2 border border-white/50 p-6 md:p-8 lg:p-10"
          >
            <h2 className="font-poppins font-[700] text-darkblue text-[6vw] sm:text-[4.5vw] lg:text-[2.5vw] leading-[1.2] mb-6 bg-gradient-to-r from-darkblue to-primary bg-clip-text text-transparent">
              What is Livmo Health?
            </h2>

            <div className="space-y-5 text-text1/90 font-poppins text-[3.8vw] sm:text-[2.2vw] lg:text-[1.05vw] leading-[1.7]">
              <p>
                Livmo is India's first healthcare coordination platform designed to
                support our subscribers in accessing trusted healthcare services.
              </p>
              <p>
                Instead of searching for hospitals, clinics, labs, or homecare
                services on your own, Livmo helps you find the best healthcare
                providers near you.
              </p>
              <p>
                Our team coordinates everything for you — from finding the right
                hospital to booking appointments and estimating treatment costs.
              </p>
            </div>
          </div>

          {/* Right side - 4 Cards in 2x2 Grid */}
          <div 
            data-aos="fade-left"
            data-aos-duration="800"
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {/* Card 1: Verified Providers */}
            <div className="group bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-royalBlue/10 p-6 border border-gray-100 hover:border-royalBlue/20 transition-all duration-500 hover:-translate-y-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-royalBlue/10 to-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-4">
                <Building2 size={32} className="text-royalBlue" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins font-[700] text-darkblue text-[4.5vw] sm:text-[2.8vw] lg:text-[1.3vw] mb-2 group-hover:text-royalBlue transition-colors">
                Verified Providers
              </h3>
              <p className="text-text1/80 font-poppins text-[3.5vw] sm:text-[2vw] lg:text-[0.9vw] leading-relaxed">
                Access trusted hospitals, clinics, labs, and healthcare partners.
              </p>
            </div>

            {/* Card 2: Healthcare Guidance */}
            <div className="group bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-royalBlue/10 p-6 border border-gray-100 hover:border-royalBlue/20 transition-all duration-500 hover:-translate-y-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-royalBlue/10 to-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-4">
                <Stethoscope size={32} className="text-royalBlue" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins font-[700] text-darkblue text-[4.5vw] sm:text-[2.8vw] lg:text-[1.3vw] mb-2 group-hover:text-royalBlue transition-colors">
                Healthcare Guidance
              </h3>
              <p className="text-text1/80 font-poppins text-[3.5vw] sm:text-[2vw] lg:text-[0.9vw] leading-relaxed">
                Get help finding the right healthcare solution based on your needs.
              </p>
            </div>

            {/* Card 3: Appointment Support */}
            <div className="group bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-royalBlue/10 p-6 border border-gray-100 hover:border-royalBlue/20 transition-all duration-500 hover:-translate-y-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-royalBlue/10 to-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-4">
                <CalendarCheck size={32} className="text-royalBlue" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins font-[700] text-darkblue text-[4.5vw] sm:text-[2.8vw] lg:text-[1.3vw] mb-2 group-hover:text-royalBlue transition-colors">
                Appointment Support
              </h3>
              <p className="text-text1/80 font-poppins text-[3.5vw] sm:text-[2vw] lg:text-[0.9vw] leading-relaxed">
                Livmo helps coordinate bookings and healthcare appointments quickly.
              </p>
            </div>

            {/* Card 4: End-to-End Coordination */}
            <div className="group bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-royalBlue/10 p-6 border border-gray-100 hover:border-royalBlue/20 transition-all duration-500 hover:-translate-y-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-royalBlue/10 to-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 mb-4">
                <BadgeCheck size={32} className="text-royalBlue" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins font-[700] text-darkblue text-[4.5vw] sm:text-[2.8vw] lg:text-[1.3vw] mb-2 group-hover:text-royalBlue transition-colors">
                End-to-End Coordination
              </h3>
              <p className="text-text1/80 font-poppins text-[3.5vw] sm:text-[2vw] lg:text-[0.9vw] leading-relaxed">
                From provider selection to treatment estimates, everything is coordinated.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default Aboutus;